import { jwtDecode } from 'jwt-decode';
import { useMemo } from 'react';
import { useAppAuth } from './use-app-auth';

export function useKeycloakRoles(clientId = null) {
  const auth = useAppAuth();

  const decodedToken = useMemo(() => {
    if (!auth.user?.access_token) return null;
    
    try {
      return jwtDecode(auth.user?.access_token);
    } catch (error) {
      console.error('Error decodificando token:', error);
      return null;
    }
  }, [auth.user?.access_token]);

  // Si no se proporciona clientId, usar el primero disponible
  const detectedClientId = useMemo(() => {
    if (clientId) return clientId;
    
    if (decodedToken?.resource_access) {
      const availableClients = Object.keys(decodedToken.resource_access);
      if (availableClients.length > 0) {
        const detected = availableClients[0];
        return detected;
      }
    }
    
    return null;
  }, [decodedToken, clientId]);

  const getResourceRoles = (specificClientId = null) => {
    const targetClientId = specificClientId || detectedClientId;
    
    if (!decodedToken?.resource_access) {
      return [];
    }

    if (!targetClientId) {
      console.warn('❌ No se proporcionó clientId');
      return [];
    }

    // resource_access es un OBJETO, accedemos por clave
    const clientRoles = decodedToken.resource_access[targetClientId];
    
    if (!clientRoles) {
      console.warn(`❌ No se encontró el cliente "${targetClientId}"`);
      console.log('Clientes disponibles:', Object.keys(decodedToken.resource_access));
      return [];
    }

    return clientRoles.roles || [];
  };

  const getRealmRoles = () => {
    return decodedToken?.realm_access?.roles || [];
  };

  const hasResourceRole = (role, specificClientId = null) => {
    const roles = getResourceRoles(specificClientId);
    return roles.includes(role);
  };

  const hasRealmRole = (role) => {
    return getRealmRoles().includes(role);
  };

  const hasAnyResourceRole = (roles, specificClientId = null) => {
    const userRoles = getResourceRoles(specificClientId);
    return roles.some(role => userRoles.includes(role));
  };

  const getAllClientIds = () => {
    return decodedToken?.resource_access ? Object.keys(decodedToken.resource_access) : [];
  };

  const getAllResourceRoles = () => {
    if (!decodedToken?.resource_access) return {};
    
    // Retorna un objeto con todos los clientes y sus roles
    return decodedToken.resource_access;
  };

  return {
    decodedToken,
    clientId: detectedClientId,
    resourceRoles: getResourceRoles(),
    realmRoles: getRealmRoles(),
    hasResourceRole,
    hasRealmRole,
    hasAnyResourceRole,
    getResourceRoles, // función para obtener roles de cualquier cliente
    getAllClientIds,
    getAllResourceRoles,
    isAuthenticated: auth.isAuthenticated,
  };
}